import { notifications } from '@mantine/notifications';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '~/shared/api/queryKeys';
import {
    type PayoutAdminTraderV1BlockTraderRequest,
    type PayoutAdminTraderV1ActivateTraderRequest,
    type PayoutAdminTraderV1UnblockTraderRequest,
    type AdminPayoutTraderServiceListTradersParams,
    type PayoutAdminTraderV1UpdateTraderConfigV2Request,
    type PayoutAdminTraderV1SetTraderWorkingStatusRequest,
    type AdminPayoutTraderServiceGetTraderParams,
    type AdminPayoutTraderServiceGetTraderBalanceParams,
    type AdminPayoutTraderServiceGetTraderRateParams,
    type PayoutAdminTraderV1UpdateTraderRateRequest,
} from '~/generated/payoutBackend/model';
import {
    adminPayoutTraderServiceActivateTrader,
    adminPayoutTraderServiceBlockTrader,
    adminPayoutTraderServiceGetTrader,
    adminPayoutTraderServiceGetTraderBalance,
    adminPayoutTraderServiceGetTraderRate,
    adminPayoutTraderServiceListTraders,
    adminPayoutTraderServiceSetTraderWorkingStatus,
    adminPayoutTraderServiceUnblockTrader,
    adminPayoutTraderServiceUpdateTraderConfigV2,
    adminPayoutTraderServiceUpdateTraderRate,
    getAdminPayoutTraderServiceGetTraderBalanceUrl,
    getAdminPayoutTraderServiceGetTraderRateUrl,
    getAdminPayoutTraderServiceGetTraderUrl,
    getAdminPayoutTraderServiceListTradersUrl,
} from '~/generated/payoutBackend/trader';
import { type WorkStrategy, type TraderStatus, type WorkingStatus } from '../model';

interface UseTraderMutationProps {
    onError?: (error: Error) => void;
    onSuccess?: () => void;
}

export const useActivateTrader = (options?: UseTraderMutationProps) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (payload: PayoutAdminTraderV1ActivateTraderRequest) => {
            return await adminPayoutTraderServiceActivateTrader(payload);
        },
        onError: options?.onError,
        onSuccess: () => {
            queryClient
                .invalidateQueries({
                    queryKey: [queryKeys.PAYOUT_TRADERS],
                })
                .then(() => {
                    options?.onSuccess?.();
                    notifications.show({
                        color: 'green',
                        title: 'Успешно',
                        message: 'Аккаунт активирован',
                    });
                });
        },
    });
};

export const useBlockTrader = (options?: UseTraderMutationProps) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (payload: PayoutAdminTraderV1BlockTraderRequest) => {
            return await adminPayoutTraderServiceBlockTrader(payload);
        },
        onError: options?.onError,
        onSuccess: () => {
            queryClient
                .invalidateQueries({
                    queryKey: [queryKeys.PAYOUT_TRADERS],
                })
                .then(() => {
                    options?.onSuccess?.();
                    notifications.show({
                        color: 'green',
                        title: 'Успешно',
                        message: 'Аккаунт заблокирован',
                    });
                });
        },
    });
};

export const useUnBlockTrader = (options?: UseTraderMutationProps) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (payload: PayoutAdminTraderV1UnblockTraderRequest) => {
            return await adminPayoutTraderServiceUnblockTrader(payload);
        },
        onError: options?.onError,
        onSuccess: () => {
            queryClient
                .invalidateQueries({
                    queryKey: [queryKeys.PAYOUT_TRADERS],
                })
                .then(() => {
                    options?.onSuccess?.();
                    notifications.show({
                        color: 'green',
                        title: 'Успешно',
                        message: 'Аккаунт заблокирован',
                    });
                });
        },
    });
};

type TradersParams = Omit<
    AdminPayoutTraderServiceListTradersParams,
    'filter.statuses' | 'filter.workingStatuses' | 'filter.workStrategies'
> & {
    'filter.statuses'?: TraderStatus[];
    'filter.workingStatuses'?: WorkingStatus[];
    'filter.workStrategies'?: WorkStrategy[];
};

interface UseTraderBalanceOptions {
    enabled?: boolean;
    refetchInterval?: number | false;
}

interface UseTraderRateOptions {
    enabled?: boolean;
    retry?: number | false;
}

const traderRateRetryCount = 4;

export const useTradersList = (params?: TradersParams, refetchInterval?: number | false) => {
    return useQuery({
        refetchInterval,
        refetchOnWindowFocus: true,
        refetchOnMount: true,
        queryFn: () => adminPayoutTraderServiceListTraders(params as AdminPayoutTraderServiceListTradersParams),
        queryKey: [
            queryKeys.PAYOUT_TRADERS,
            getAdminPayoutTraderServiceListTradersUrl(params as AdminPayoutTraderServiceListTradersParams),
        ],
    });
};

export const useSetTraderWorkingStatus = (options?: UseTraderMutationProps) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (payload: PayoutAdminTraderV1SetTraderWorkingStatusRequest) => {
            return await adminPayoutTraderServiceSetTraderWorkingStatus(payload);
        },
        onError: options?.onError,
        onSuccess: () => {
            options?.onSuccess?.();
            notifications.show({
                color: 'green',
                title: 'Успешно',
                message: 'Статус работы обновлён',
            });
            queryClient.invalidateQueries({
                queryKey: [queryKeys.PAYOUT_TRADERS, queryKeys.PAYOUT_TRADER],
            });
        },
    });
};

export const useUpdateTraderConfig = (options?: UseTraderMutationProps) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (payload: PayoutAdminTraderV1UpdateTraderConfigV2Request) => {
            return await adminPayoutTraderServiceUpdateTraderConfigV2(payload);
        },
        onError: options?.onError,
        onSuccess: () => {
            queryClient
                .invalidateQueries({
                    queryKey: [queryKeys.PAYOUT_TRADERS],
                })
                .then(() => {
                    options?.onSuccess?.();
                    notifications.show({
                        color: 'green',
                        title: 'Успешно',
                        message: 'Конфигурация обновлена',
                    });
                });
        },
    });
};

export const useUpdateTraderRate = (options?: UseTraderMutationProps) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (payload: PayoutAdminTraderV1UpdateTraderRateRequest) => {
            return await adminPayoutTraderServiceUpdateTraderRate(payload);
        },
        onError: options?.onError,
        onSuccess: () => {
            queryClient
                .invalidateQueries({
                    queryKey: [queryKeys.PAYOUT_TRADERS],
                })
                .then(() => {
                    options?.onSuccess?.();
                    notifications.show({
                        color: 'green',
                        title: 'Успешно',
                        message: 'Комиссия обновлена',
                    });
                });
        },
    });
};

export const useTrader = (params: AdminPayoutTraderServiceGetTraderParams) => {
    return useQuery({
        refetchOnWindowFocus: true,
        refetchOnMount: true,
        queryFn: () => adminPayoutTraderServiceGetTrader(params),
        queryKey: [queryKeys.PAYOUT_TRADERS, queryKeys.PAYOUT_TRADER, getAdminPayoutTraderServiceGetTraderUrl(params)],
    });
};

export const useTraderBalance = (
    params?: AdminPayoutTraderServiceGetTraderBalanceParams,
    options?: UseTraderBalanceOptions,
) => {
    const isEnabled = Boolean(params?.['traderId.value']) && (options?.enabled ?? true);

    return useQuery({
        enabled: isEnabled,
        refetchInterval: options?.refetchInterval,
        refetchOnWindowFocus: true,
        refetchOnMount: true,
        queryFn: () => adminPayoutTraderServiceGetTraderBalance(params),
        queryKey: [
            queryKeys.PAYOUT_TRADERS,
            queryKeys.PAYOUT_TRADER_BALANCE,
            getAdminPayoutTraderServiceGetTraderBalanceUrl(params),
        ],
    });
};

export const useTraderRate = (params?: AdminPayoutTraderServiceGetTraderRateParams, options?: UseTraderRateOptions) => {
    const isEnabled = Boolean(params?.['traderId.value']) && (options?.enabled ?? true);

    return useQuery({
        enabled: isEnabled,
        retry: options?.retry ?? traderRateRetryCount,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
        refetchOnMount: true,
        queryFn: () => adminPayoutTraderServiceGetTraderRate(params),
        queryKey: [
            queryKeys.PAYOUT_TRADERS,
            queryKeys.PAYOUT_TRADER_RATE,
            getAdminPayoutTraderServiceGetTraderRateUrl(params),
        ],
    });
};
